import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  aggregateMastery,
  dueConcepts,
  freezesHeld,
  levelForXp,
  recentAccuracy,
  sortForStudent,
  streakStatus,
  type Assignment,
} from '@yuan-math/learning';
import { Card, Mascot, MasteryBar, SectionHeading, Stat } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { DailyQuests } from '../components/DailyQuests.tsx';
import { StudentAssignments } from '../components/Assignments.tsx';
import { AdventureCallout } from '../components/AdventureCallout.tsx';
import { fetchStudentAssignments } from '../state/classroom.ts';

/**
 * The signed-in home.
 *
 * One question is answered above everything else: *what should I do next?*
 * The recommendation engine answers it, and the reason is always shown —
 * a student should never be told to do something without being told why.
 */
export function DashboardPage(): JSX.Element {
  const { profile, recommendations, masteredConceptIds, quests, weekly } = useLearner();

  // Assigned work is fetched here rather than in LearnerContext: it belongs to
  // a class, not to the learner, and a student with no teacher has none.
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  useEffect(() => {
    let cancelled = false;
    void fetchStudentAssignments().then((rows) => {
      if (!cancelled) setAssignments(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!profile) return <div />;

  const now = Date.now();
  const level = levelForXp(profile.xp);
  const accuracy = recentAccuracy(profile);
  const due = dueConcepts(profile.concepts, now);
  const available = registry.availableConcepts();
  const overall = aggregateMastery(available.map((c) => c.id), profile.concepts, now);
  const streak = streakStatus(profile.streak, now);
  const restDays = freezesHeld(profile.streak);

  /**
   * A learner who declared a level above elementary but has neither taken the
   * diagnostic nor practised anything sees only the root of the graph — which
   * is honest, but useless to a fourteen-year-old. The placement test is how
   * the product intends them to skip ahead (§27), so say so.
   */
  const needsPlacement =
    profile.diagnostic === undefined &&
    profile.stats.totalAttempts === 0 &&
    !['kindergarten', 'elementary'].includes(profile.ageBand);

  const strandRows = registry.strands
    .map((strand) => {
      const concepts = registry.conceptsInStrand(strand.id).filter((c) => c.status === 'available');
      if (concepts.length === 0) return null;
      return {
        strand,
        mastery: aggregateMastery(concepts.map((c) => c.id), profile.concepts, now),
        count: concepts.length,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)
    .sort((a, b) => b.mastery - a.mastery);

  return (
    <div className="page page--wide">
      <header className="dashboard-header">
        <div className="dashboard-header__greeting">
          {/* Sigma is idle here and reacting on the practice page — the same
              character in both places is what makes the app feel inhabited. */}
          <Mascot mood="idle" size={72} />
          <div>
            <p className="dashboard-header__eyebrow">
              {greeting()}, {profile.displayName}
            </p>
            <h1 className="page__title">
              {recommendations[0] ? 'Here is what to do next.' : 'Ready when you are.'}
            </h1>
          </div>
        </div>
        <div className="dashboard-stats">
          <Stat label="Overall mastery" value={`${Math.round(overall * 100)}%`} hint={`across ${available.length} concepts`} />
          <Stat
            label="Streak"
            value={`${profile.streak.current} ${profile.streak.current === 1 ? 'day' : 'days'}`}
            hint={streakHint(streak, restDays)}
          />
          <Stat label="Level" value={level.level} hint={`${level.title} · ${profile.xp} XP`} />
          {accuracy !== undefined ? <Stat label="Recent accuracy" value={`${Math.round(accuracy * 100)}%`} hint="last 50 questions" /> : null}
        </div>
      </header>

      {needsPlacement ? (
        <section className="dashboard-section">
          <Card className="placement-nudge">
            <div>
              <h2 className="placement-nudge__title">Start higher up?</h2>
              <p className="placement-nudge__body">
                Nothing is unlocked above the very beginning yet, because you have not shown us what you already
                know. The placement test walks the concept graph and opens everything you can already do — it
                usually takes under ten questions.
              </p>
            </div>
            <Link to="/diagnostic" className="btn btn--primary">
              Take the placement test
            </Link>
          </Card>
        </section>
      ) : null}

      {/* A teacher's instruction outranks the platform's own suggestion. */}
      <StudentAssignments entries={sortForStudent(assignments, profile, now)} />

      {/* The way into the adventure, and only that: everything below is
          unchanged and stays the first-class way to use the platform. */}
      <AdventureCallout profile={profile} now={now} />

      <section className="dashboard-section">
        <SectionHeading
          title="What to do next"
          description="Chosen from your mastery, what you are forgetting, and what your current work depends on."
        />
        {recommendations.length === 0 ? (
          <Card>
            <p>
              Nothing pressing. <Link to="/map">Explore the map</Link> and pick something that interests you.
            </p>
          </Card>
        ) : (
          <ul className="recommendation-list">
            {recommendations.map((recommendation) => {
              const concept = registry.concept(recommendation.conceptId);
              if (!concept) return null;
              return (
                <li key={`${recommendation.kind}-${recommendation.conceptId}`}>
                  <Card className="recommendation">
                    <div className="recommendation__body">
                      <span className={`recommendation__kind recommendation__kind--${recommendation.kind}`}>
                        {kindLabel(recommendation.kind)}
                      </span>
                      <h3 className="recommendation__title">{concept.title}</h3>
                      <p className="recommendation__reason">{recommendation.reason}</p>
                    </div>
                    <div className="recommendation__actions">
                      {recommendation.lessonId ? (
                        <Link to={`/lessons/${recommendation.lessonId}`} className="btn btn--primary btn--sm">
                          {recommendation.kind === 'review' ? 'Review' : 'Open lesson'}
                        </Link>
                      ) : null}
                      <Link to={`/practice/${recommendation.conceptId}`} className="btn btn--secondary btn--sm">
                        Practise
                      </Link>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <DailyQuests
        quests={quests}
        weekly={weekly}
        {...(recommendations[0] ? { practiceLink: `/practice/${recommendations[0].conceptId}` } : {})}
      />

      {due.length > 0 ? (
        <section className="dashboard-section">
          <SectionHeading
            title="Due for review"
            description="Spaced practice, timed to catch these just as they start to fade."
          />
          <ul className="chip-list chip-list--large">
            {due.slice(0, 8).map((conceptId) => (
              <li key={conceptId}>
                <Link to={`/practice/${conceptId}`} className="chip chip--due">
                  {registry.concept(conceptId)?.title ?? conceptId}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="dashboard-section">
        <SectionHeading
          title="Your mathematics profile"
          description="Mastery by strand. Weak areas are not failures — they are simply what to do next."
          action={
            <Link to="/progress" className="btn btn--ghost btn--sm">
              Full progress
            </Link>
          }
        />
        <div className="strand-grid">
          {strandRows.map((row) => (
            <Card key={row.strand.id} className="strand-card">
              <div className="strand-card__head">
                <h3>{row.strand.title}</h3>
                <span className="strand-card__count">{row.count} concepts</span>
              </div>
              <MasteryBar value={row.mastery} />
              <p className="strand-card__description">{row.strand.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeading title="Keep going" />
        <div className="quick-links">
          <Link to="/courses" className="quick-link">
            <strong>Courses</strong>
            <span>Follow a structured path through a grade.</span>
          </Link>
          <Link to="/map" className="quick-link">
            <strong>Math Map</strong>
            <span>See the whole subject and where you sit in it.</span>
          </Link>
          <Link to="/diagnostic" className="quick-link">
            <strong>Placement test</strong>
            <span>Re-check your level whenever you like.</span>
          </Link>
          <Link to="/parent" className="quick-link">
            <strong>Parent view</strong>
            <span>A weekly summary, without the noise.</span>
          </Link>
        </div>
      </section>

      <p className="dashboard-note">
        {masteredConceptIds.size} concepts mastered · {profile.stats.lessonsCompleted} lessons finished ·{' '}
        {profile.stats.totalAttempts} questions answered
      </p>
    </div>
  );
}

/**
 * What to say about the streak.
 *
 * A student who missed yesterday and has cover banked is told they have not
 * lost it, because they have not — saying otherwise would be both untrue and
 * the fastest way to make them stop.
 */
function streakHint(status: ReturnType<typeof streakStatus>, restDays: number): string {
  const cover = restDays > 0 ? ` \u00b7 ${restDays} rest ${restDays === 1 ? 'day' : 'days'} banked` : '';
  switch (status) {
    case 'safe':
      return `today is counted${cover}`;
    case 'at-risk':
      return `keep it alive today${cover}`;
    case 'rescuable':
      return 'a rest day is holding it \u2014 practise today';
    case 'broken':
      return 'start a new one today';
    default:
      return 'start one today';
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'repair':
      return 'Fix a foundation';
    case 'review':
      return 'Review';
    case 'continue':
      return 'Continue';
    default:
      return 'Something new';
  }
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
